import { Analytics, logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { analyticsPromise } from './firebase';

// Web equivalent of Platform.OS
const getPlatform = (): string => {
  const ua = navigator.userAgent.toLowerCase();
  if (/android/.test(ua)) {return 'android';}
  if (/iphone|ipad|ipod/.test(ua)) {return 'ios';}
  return 'web';
};

// Cached analytics instance
let _analytics: Analytics | null = null;
analyticsPromise.then((a) => { _analytics = a; });

const getAnalyticsInstance = async (): Promise<Analytics | null> => {
  if (_analytics) {return _analytics;}
  _analytics = await analyticsPromise;
  return _analytics;
};

// ─────────────────────────────────────────────
// 1. SET ANALYTICS USER
// ─────────────────────────────────────────────
export const setAnalyticsUser = async (
  userId: string,
  mobileNumber?: string,
): Promise<void> => {
  try {
    //console.log("Setting Analytics User:", userId);

    const analytics = await getAnalyticsInstance();
    if (!analytics) {return;}

    await setUserId(analytics, userId);

    await setUserProperties(analytics, {
      mobile_number: mobileNumber || '',
      device_platform: getPlatform(),
    });

  } catch (error) {
    //console.log("Set User Error:", error);
  }
};

// ─────────────────────────────────────────────
// EVENT TRACKER
// ─────────────────────────────────────────────
export const trackEvent = async (
  eventName: string,
  params?: any,
  userId?: string,
  mobileNumber?: string,
): Promise<void> => {
  try {
    const eventParams = {
      platform: getPlatform(),
      ...(params || {}),
      ...(userId ? { user_id: userId } : {}),
      ...(mobileNumber ? { mobile_number: mobileNumber } : {}),
    };

    //console.log("Analytics Event:", eventName, eventParams);

    const analytics = await getAnalyticsInstance();
    if (!analytics) {return;}

    logEvent(analytics, eventName, eventParams);

  } catch (error) {
    //console.log(" Analytics Error:", error);
  }
};

// ─────────────────────────────────────────────
// SCREEN TRACKING
// ─────────────────────────────────────────────
export const trackScreen = async (
  screenName: string,
  userId?: string,
  mobileNumber?: string,
): Promise<void> => {
  try {
    //console.log("Screen View:", screenName);

    const analytics = await getAnalyticsInstance();
    if (!analytics) {return;}

    // Equivalent of analytics().logScreenView()
    logEvent(analytics, 'screen_view', {
      firebase_screen: screenName,
      firebase_screen_class: screenName,
    });

    await trackEvent(
      'screen_view_custom',
      { screen_name: screenName },
      userId,
      mobileNumber,
    );

  } catch (error) {
    //console.log(" Screen Tracking Error:", error);
  }
};

// ─────────────────────────────────────────────
// 4. BUTTON CLICK TRACKING
// ─────────────────────────────────────────────
export const trackButtonClick = async (
  buttonName: string,
  screenName: string,
  userId?: string,
  mobileNumber?: string,
): Promise<void> => {
  //console.log("Button Click:", buttonName);

  await trackEvent(
    'button_click',
    {
      button_name: buttonName,
      screen_name: screenName,
    },
    userId,
    mobileNumber,
  );
};
